import { getDownloadAccess } from '../utils/downloadAccess';
import { useEffect, useState, useRef } from 'react';
import { supabase } from '../supabase';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import AdBanner from '../components/AdBanner';
import DownloadPromptModal from '../components/DownloadPromptModal';
import AudioDownloadModal from '../components/AudioDownloadModal';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { handleSongDownload } from '../utils/downloadHelper';
import { Helmet } from 'react-helmet-async';
import '../styles.css';

function getSongJsonLd(song) {
  return {
    "@context": "https://schema.org",
    "@type": "MusicComposition",
    "name": song.title,
    "composer": song.composer || "Unknown Composer",
    "datePublished": song.created_at,
    "inLanguage": "en",
    "url": `https://choircenter.com/song/${song.permalink || song.id}`,
    "description": song.description
      ? song.description.replace(/<[^>]+>/g, '').slice(0, 180)
      : "Sheet music, lyrics, and audio preview.",
    "offers": {
      "@type": "Offer",
      "price": "0.00",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    ...(song.audio_url && {
      "audio": {
        "@type": "AudioObject",
        "contentUrl": song.audio_url,
        "encodingFormat": "audio/mpeg"
      }
    }),
    ...(song.google_drive_file_id && {
      "associatedMedia": {
        "@type": "MediaObject",
        "contentUrl": `https://drive.google.com/uc?export=download&id=${song.google_drive_file_id}`,
        "encodingFormat": "application/pdf",
        "name": `${song.title} - Sheet Music PDF`
      },
      "image": `https://drive.google.com/thumbnail?id=${song.google_drive_file_id}&sz=s500`
    }),
    ...(song.description && {
      "lyrics": {
        "@type": "CreativeWork",
        "text": song.description.replace(/<[^>]+>/g, '')
      }
    }),
    "potentialAction": {
      "@type": "DownloadAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `https://drive.google.com/uc?export=download&id=${song.google_drive_file_id}`,
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      }
    }
  };
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// --- ADD THIS FUNCTION ---
// (can go anywhere inside the component or above return)
function getYoutubeEmbedUrl(url) {
  if (!url) return '';
  // Accepts standard YouTube URLs and share links
  // e.g. https://www.youtube.com/watch?v=XXXXX or https://youtu.be/XXXXX
  let videoId = '';
  try {
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/))([\w-]{11})/
    );
    videoId = match ? match[1] : '';
  } catch {}
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
}

function Song() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [song, setSong] = useState(null);
  const [relatedSongs, setRelatedSongs] = useState([]);
  const [error, setError] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptType, setPromptType] = useState('register');
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const [user, setUser] = useState(null);
  const [hasDonated, setHasDonated] = useState(false);
  const [downloadCountThisMonth, setDownloadCountThisMonth] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Always read lastLibraryPage from URL param (with fallback to 1)
  const searchParams = new URLSearchParams(location.search);
  const lastLibraryPage = searchParams.get('fromPage') && !isNaN(Number(searchParams.get('fromPage')))
    ? parseInt(searchParams.get('fromPage'), 10)
    : 1;

  useEffect(() => {
    const lastPage = parseInt(sessionStorage.getItem('lastLibraryPage'), 10);
    const lastScroll = parseInt(sessionStorage.getItem('lastLibraryScroll'), 10);

    if (lastScroll && lastPage === lastLibraryPage) {
      setTimeout(() => {
        window.scrollTo(0, lastScroll);
        sessionStorage.removeItem('lastLibraryScroll');
      }, 100);
    }
    // eslint-disable-next-line
  }, [lastLibraryPage]);

  useEffect(() => {
    const fetchSong = async () => {
      let songQuery = supabase
        .from('songs')
        .select('*')
        .eq('is_public', true);

      if (/^\d+$/.test(id)) {
        songQuery = songQuery.eq('id', parseInt(id, 10));
      } else {
        songQuery = songQuery.eq('permalink', id.toLowerCase());
      }

      const { data: songData, error: songError } = await songQuery.single();
      if (songError) {
        setError('Failed to load song: ' + songError.message);
        return;
      }
      setSong(songData);
      setPdfProgress(100);

      // Related songs
      const titleWords = songData.title.toLowerCase().split(/\s+/).filter(w => w.length > 2);
      let relatedQuery = supabase
        .from('songs')
        .select('id, title, composer, permalink, created_at')
        .eq('is_public', true)
        .neq('id', songData.id);

      const orConditions = [];
      if (titleWords.length > 0) {
        orConditions.push(...titleWords.map(w => `title.ilike.%${w}%`));
      }
      if (songData.composer) {
        orConditions.push(`composer.eq.${songData.composer}`);
      }
      if (orConditions.length > 0) {
        relatedQuery = relatedQuery.or(orConditions.join(','));
      }

      const { data: relatedData } = await relatedQuery
        .order('downloads', { ascending: false })
        .limit(5);
      setRelatedSongs(relatedData || []);
    };

    fetchSong();
  }, [id]);

  useEffect(() => {
    const fetchAuthAndProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        // Fetch full profile for subscription info
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        setUser({ ...session.user, ...profile });

        // Admin check
        const { data: adminRow } = await supabase
          .from('admins')
          .select('user_id')
          .eq('user_id', session.user.id)
          .single();
        setIsAdmin(!!adminRow);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    };
    fetchAuthAndProfile();
  }, []);

  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;
      setScale(width <= 480 ? 0.5 : width <= 768 ? 0.75 : 1.0);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateProgress = () => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      audio.addEventListener('timeupdate', updateProgress);
      return () => audio.removeEventListener('timeupdate', updateProgress);
    }
  }, [song]);

  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const handleDownload = async () => {
    if (!song) return;
    const result = await handleSongDownload(song, user, setPromptType, setShowPrompt);

    if (!result.allowed) return; // If not allowed, prompt will be shown

    window.open(`https://drive.google.com/uc?export=download&id=${song.google_drive_file_id}`, '_blank');
    setSong(result.updatedSong);
  };

  const handleAudioDownload = () => {
    if (!song || !song.audio_url) return;

    // Check for active subscription (supporter or expert)
    const isSubscribed =
      (user?.subscription_tier === 'choir_supporter' || user?.subscription_tier === 'choir_expert') &&
      user?.subscription_expires_at &&
      new Date(user.subscription_expires_at) > new Date();

    if (!isSubscribed) {
      setShowAudioPrompt(true);
      return;
    }

    // If allowed, trigger download
    const link = document.createElement('a');
    link.href = song.audio_url;
    link.download = `${song.title}-${song.id}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(() => {});
      }
      setIsPlaying(!isPlaying);
    }
  };
  const rewind = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleShare = () => {
    if (!song) return;
    const shareUrl = `${window.location.origin}/song/${song.permalink || song.id}?fromPage=${lastLibraryPage}`;
    const shareText = `Check out "${song.title}" on Choir Center!`;
    if (navigator.share) {
      navigator.share({ title: song.title, text: shareText, url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => alert('Link copied to clipboard!'))
        .catch(() => {});
    }
  };

  const thumbnailUrl = song?.google_drive_file_id
    ? `https://drive.google.com/thumbnail?id=${song.google_drive_file_id}&sz=s500`
    : null;

  if (error) {
    return (
      <div className="song-container">
        <p className="error-message">{error}</p>
        <Link to={`/library?page=${lastLibraryPage}`} className="action-button">Back to Library</Link>
      </div>
    );
  }
  if (!song) {
    return (
      <div className="loading-container-modern">
        <div className="spinner-modern"></div>
      </div>
    );
  }

  return (
    <>
      {song && (
        <Helmet>
          <title>{song.title} | {song.composer || 'Unknown Composer'} | Choir Center</title>
          <meta name="description" content={song.description ? song.description.replace(/<[^>]+>/g, '').slice(0, 160) : "Sheet music, lyrics, and audio preview for choir songs."} />
          <meta property="og:title" content={`${song.title} | ${song.composer || 'Unknown Composer'} | Choir Center`} />
          <meta property="og:description" content={song.description ? song.description.replace(/<[^>]+>/g, '').slice(0, 160) : "Sheet music, lyrics, and audio preview."} />
          <meta property="og:image" content={song.google_drive_file_id ? `https://drive.google.com/thumbnail?id=${song.google_drive_file_id}&sz=s500` : ''} />
          <meta property="og:url" content={`https://choircenter.com/song/${(song.permalink || song.id).toLowerCase()}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <link
            rel="canonical"
            href={`https://choircenter.com/song/${(song.permalink || song.id).toLowerCase()}`}
          />
          <script type="application/ld+json">
            {JSON.stringify(getSongJsonLd(song))}
          </script>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://choircenter.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Library",
                  "item": "https://choircenter.com/library"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": song.title,
                  "item": `https://choircenter.com/song/${(song.permalink || song.id).toLowerCase()}`
                }
              ]
            })}
          </script>          
        </Helmet>
      )}

      <section className="ad-section">
        <AdBanner position="song_page_below_header" />
      </section>
      <div className="song-container">
        <div className="song-section-container">
            <nav aria-label="Breadcrumb" className="breadcrumb-nav" style={{ marginBottom: "1rem" }}>
              <ol style={{ display: "flex", gap: "0.5rem", fontSize: "0.95rem", flexWrap: "wrap", justifyContent: "center" }}>
                <li>
                  <Link to="/" style={{ color: "#26732e", textDecoration: "none" }}>Home</Link>
                  <span aria-hidden="true" style={{ margin: "0 0.4rem" }}>/</span>
                </li>
                <li>
                  <Link to="/library" style={{ color: "#26732e", textDecoration: "none" }}>Library</Link>
                  <span aria-hidden="true" style={{ margin: "0 0.4rem" }}>/</span>
                </li>
                <li style={{ color: "#000", fontWeight: "bold" }}>
                  {song.title}
                </li>
              </ol>
            </nav>
          <div className="song-info-container" style={{ textAlign: 'center' }}>
            <h1 className="song-title-modern">{song.title}</h1>
            <h2 className="song-composer-modern" style={{ fontSize: "1.2rem", fontWeight: "normal", margin: 0 }}>
              {song.composer || 'Unknown Composer'}
            </h2>
            <p className="song-downloads-modern">Downloaded {song.downloads || 0} times</p>
            <p className="song-timestamp-modern">Added on {new Date(song.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>
        {song.audio_url && (
          <div className="song-section-container">
            <div className="w-full flex flex-col items-center justify-center">
              <div
                className="flex items-center gap-3 px-4 py-2 rounded-xl bg-gradient-to-br from-green-950 via-green-900 to-black shadow-2xl ring-2 ring-green-600/70 hover:ring-4 transition-all duration-200"
                style={{
                  maxWidth: 380,
                  width: "100%",
                  minHeight: 54,
                  margin: "0 auto",
                  boxShadow: "0 0 16px 4px #22d3ee55, 0 0 40px 8px #22c55e33"
                }}
              >
                <button
                  onClick={rewind}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-green-900 text-white shadow-lg hover:scale-110 hover:shadow-green-500/40 hover:bg-green-800 active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-150"
                  aria-label="Rewind"
                >⏪</button>
                <button
                  onClick={togglePlay}
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-gradient-to-br from-green-800 to-black text-2xl text-white font-bold shadow-lg hover:scale-110 hover:shadow-green-500/50 hover:bg-green-900 active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-150"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? '⏸' : '▶'}
                </button>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={progress}
                  onChange={e => {
                    if (audioRef.current) {
                      const newTime = (e.target.value / 100) * audioRef.current.duration;
                      audioRef.current.currentTime = newTime;
                      setProgress(e.target.value);
                    }
                  }}
                  className="flex-1 mx-3 h-2 rounded-lg bg-black/40 accent-green-500 ring-1 ring-green-800 shadow-inner transition-all duration-200 cursor-pointer"
                  aria-label="Audio progress"
                  style={{
                    accentColor: "#22c55e"
                  }}
                />
                <audio
                  ref={audioRef}
                  src={song.audio_url}
                  aria-label={`Audio preview for ${song.title}${song.composer ? ' by ' + song.composer : ''}`}
                  tabIndex="0"
                  preload="none"
                  controls={false}
                />
              </div>
              <button
                onClick={() => {
                  if (window.gtag) {
                    gtag('event', 'file_download', {
                      event_category: 'Downloads',
                      event_label: song?.title + ' Audio' || 'Unknown Audio',
                      value: 1,
                    });
                  }
                  handleAudioDownload();
                }}
                className="mt-2 px-5 py-1.5 rounded-full font-semibold bg-gradient-to-br from-green-900 to-black text-white shadow-md hover:scale-105 hover:bg-green-700 hover:shadow-green-400/40 focus:ring-2 focus:ring-green-400 transition-all duration-200"
              >
                Download Audio
              </button>
            </div>
          </div>
        )}
        <div className="song-section-container">
          <div className="pdf-preview-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {thumbnailUrl && !thumbnailError ? (
              <img
                src={thumbnailUrl}
                alt={`Preview of ${song.title} sheet music`}
                className="song-preview-image"
                onError={() => setThumbnailError(true)}
                style={{ display: 'block', margin: '0 auto' }}
              />
            ) : (
              <Document
                file={`/.netlify/functions/proxy-pdf?fileId=${song.google_drive_file_id}`}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<div>Loading PDF...</div>}
              >
                <Page pageNumber={1} scale={scale} style={{ display: 'block', margin: '0 auto' }} />
              </Document>
            )}
            <p className="preview-note-modern" style={{ textAlign: 'center' }}>
              Previewing page 1{numPages && thumbnailError ? ` of ${numPages}` : ''}. Click the Download button to view the full song.
            </p>
          </div>
        </div>
        <div className="song-section-container">
          <div className="song-actions-container song-actions-modern" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              onClick={() => {
                if (window.gtag) {
                  gtag('event', 'file_download', {
                    event_category: 'Downloads',
                    event_label: song?.title || 'Unknown Song',
                    value: 1,
                  });
                }
                handleDownload();
              }}
              className="download-button-modern"
              aria-label={`Download ${song.title} as PDF`}
            >
              Download
            </button>
            <button
              onClick={handleShare}
              className="share-button-modern"
              aria-label={`Share ${song.title} on social media`}
            >
              Share
            </button>
            <Link
              to={`/library?page=${lastLibraryPage}`}
              className="back-button-modern"
              onClick={() => {
                sessionStorage.setItem('lastLibraryScroll', window.scrollY.toString());
                sessionStorage.setItem('lastLibraryPage', lastLibraryPage);
              }}
            >
              Back to Library
            </Link>
            {isAdmin && (
              <button
                onClick={() => navigate(`/admin?tab=songs&editSongId=${song.id}`)}
                className="download-button-modern"
              >
                Edit Song
              </button>
            )}
          </div>
        </div>

        {/* NEW: Song Description/Lyrics Section */}
        {(song.lyrics || song.description) && (
          <section className="song-section-container" aria-label="Lyrics and Description">
            {song.lyrics && (
              <>
                <h2 className="song-description-heading" style={{ textAlign: 'center', marginBottom: 6 }}>
                  Lyrics
                </h2>
                <div
                  className="song-lyrics"
                  style={{
                    fontFamily: 'inherit',
                    fontSize: '1.09rem',
                    color: '#222',
                    lineHeight: '1.7',
                    background: '#f9f9f9',
                    borderRadius: '0.5rem',
                    padding: '1.1rem 1rem',
                    margin: '0 auto',
                    marginTop: '0.4rem',
                    marginBottom: '1.3rem',
                    boxShadow: '0 2px 8px 0 #00000010'
                  }}
                  dangerouslySetInnerHTML={{ __html: song.lyrics }}
                />
              </>
            )}
            {song.description && (
              <>
                <h2 className="song-description-heading" style={{ textAlign: 'center', marginBottom: 6 }}>
                  Song Description
                </h2>
                <div
                  className="song-description"
                  style={{
                    fontFamily: 'inherit',
                    fontSize: '1.09rem',
                    color: '#202020',
                    lineHeight: '1.7',
                    background: '#f7f9fa',
                    borderRadius: '0.5rem',
                    padding: '1.1rem 1rem',
                    margin: '0 auto',
                    marginTop: '0.4rem',
                    marginBottom: '1.3rem',
                    boxShadow: '0 2px 8px 0 #00000008'
                  }}
                  dangerouslySetInnerHTML={{ __html: song.description }}
                />
              </>
            )}
          </section>
        )}

        {/* NEW: YouTube Embed Section */}
        {song.youtube_url && (
          <section className="song-section-container" style={{ textAlign: 'center', margin: '1.6rem 0' }}>
            <h2 className="song-description-heading" style={{ textAlign: 'center', marginBottom: 10 }}>
              Watch Video
            </h2>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: 520,
                aspectRatio: '16/9',
                background: '#000',
                borderRadius: '14px',
                boxShadow: '0 2px 16px 0 #0002',
                overflow: 'hidden',
                margin: '0 auto'
              }}>
                <iframe
                  src={getYoutubeEmbedUrl(song.youtube_url)}
                  title="YouTube Video"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    minHeight: 260,
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {relatedSongs.length > 0 && (
          <div className="song-section-container">
            <div className="explore-songs-container">
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center', color: '#2f4f2f' }}>Explore More Songs</h2>
              <ul style={{ listStyle: 'none', padding: '0', display: 'grid', gap: '1rem' }}>
                {relatedSongs.filter(s => s.permalink).map((relatedSong) => (
                  <li key={relatedSong.id} style={{ background: '#fff', padding: '1rem', borderRadius: '8px', transition: 'transform 0.3s ease' }} className="animate-scale">
                    <Link
                      to={`/song/${relatedSong.permalink}?fromPage=${lastLibraryPage}`}
                      className="song-link"
                      style={{ color: '#2f4f2f', textDecoration: 'none', fontWeight: '500' }}
                      onClick={() => {
                        sessionStorage.setItem('lastLibraryPage', lastLibraryPage);
                      }}
                    >
                      {relatedSong.title} {relatedSong.composer || 'Unknown Composer'}
                    </Link>
                    <span style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                      (Added on {new Date(relatedSong.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {showPrompt && (
          <DownloadPromptModal
            onClose={() => setShowPrompt(false)}
            onRegister={() => {
              setShowPrompt(false);
              window.location.href = '/signup';
            }}
            onSubscribe={() => {
              setShowPrompt(false);
              window.open('/subscribe', '_blank');
            }}
          />
        )}
        {showAudioPrompt && (
          <AudioDownloadModal
            onClose={() => setShowAudioPrompt(false)}
            onSubscribe={() => {
              setShowAudioPrompt(false);
              window.open('/subscribe', '_blank');
            }}
          />
        )}
      </div>
    </>
  );
}

export default Song;
